<script setup lang="ts">
import { useAppStore } from '@/modules/app/stores/app.store'
import { useUserStore } from '@/modules/users/stores/user.store'
import { vOnClickOutside } from '@vueuse/components'
import { UserRoleType } from '@/modules/users/types/user.enums'
import {
  mdiMenu,
  mdiFilterMenu,
  mdiViewDashboard,
  mdiChartGantt,
  mdiAccountMultiple,
  mdiFilterOff,
} from '@mdi/js'

import OpacityBackdrop from '@/shared/components/backdrop/OpacityBackdrop.vue'
import NavLink from '@/shared/components/nav/NavLink.vue'
import NavButton from '@/shared/components/nav/NavButton.vue'
import BaseButton from '@/shared/components/button/BaseButton.vue'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'

interface Props {
  filter: boolean
}

defineProps<Props>()

const appStore = useAppStore()
const userStore = useUserStore()

const onFilterState = (state: boolean): void => {
  appStore.isFilterOpen = state
  appStore.isNavExpanded = false
}

const onExpanded = (state: boolean): void => {
  appStore.isNavExpanded = state
}
</script>

<template>
  <OpacityBackdrop class="z-20" :active="appStore.isFilterOpen || appStore.isNavExpanded" />
  <aside
    v-if="userStore.user"
    class="z-30 fixed top-0 left-0 flex items-center bg-black"
    v-on-click-outside="() => onExpanded(false)"
  >
    <div>
      <div class="px-3 pt-1.25">
        <BaseButton
          :title="$t('common.toggle')"
          @click="onExpanded(!appStore.isNavExpanded)"
          class="p-1 rounded-full hover:bg-gray-800"
        >
          <SvgIcon :path="mdiMenu" class="fill-white h-6 w-6" />
        </BaseButton>
      </div>
      <div
        class="flex flex-col gap-1 px-1 pb-1 h-[calc(100vh-63px)] transition-width linear duration-200 shadow-[0_35px_35px_rgba(0,0,0,0.25)]"
        :class="appStore.isNavExpanded ? 'w-66' : 'w-14'"
      >
        <NavButton
          :icon="filter ? mdiFilterMenu : mdiFilterOff"
          :text="$t('glossary.mySelection')"
          :active="appStore.isFilterOpen"
          :expand="appStore.isNavExpanded"
          :disabled="!filter"
          @click="onFilterState(true)"
        />
        <NavLink
          to="/"
          :text="$t('glossary.myDashboard')"
          :icon="mdiViewDashboard"
          :expand="appStore.isNavExpanded"
          @click="onFilterState(false)"
        />
        <NavLink
          to="/motion-view"
          :text="$t('glossary.motionView')"
          :icon="mdiChartGantt"
          :expand="appStore.isNavExpanded"
          @click="onFilterState(false)"
        />
        <NavLink
          v-if="userStore.user.roleId !== UserRoleType.User"
          to="/user-management"
          :text="$t('glossary.userManagement')"
          :icon="mdiAccountMultiple"
          :expand="appStore.isNavExpanded"
          class="mt-auto"
          @click="onFilterState(false)"
        />
      </div>
    </div>
  </aside>
</template>
